
import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import SEO from '../../../components/SEO';
import { getBlogPost, getRecentBlogPosts } from '../../../data/blogPosts';
import Button from '../../../components/base/Button';
import MarkdownRenderer from '../../../components/base/MarkdownRenderer';
import ScrollToTop from '../../../components/base/ScrollToTop';

export default function BlogPost() {
  const { slug } = useParams();
  const post = slug ? getBlogPost(slug) : null;
  const recentPosts = getRecentBlogPosts(3);

  // Scroll to top when component mounts or slug changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Blog indlæg ikke fundet</h1>
          <Link to="/blog">
            <Button>Tilbage til blog</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={post.title}
        description={post.excerpt}
        keywords={post.tags.join(', ')}
        image={post.image}
        url={`https://equinord.dk/blog/${post.slug}`}
        type="article"
        author={post.author}
        publishedTime={post.date}
        breadcrumbs={[
          { name: 'Hjem', url: 'https://equinord.dk/' },
          { name: 'Blog', url: 'https://equinord.dk/blog' },
          { name: post.title, url: `https://equinord.dk/blog/${post.slug}` }
        ]}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": post.title,
          "image": post.image,
          "author": {
            "@type": "Person",
            "name": post.author
          },
          "publisher": {
            "@type": "Organization",
            "name": "EquiNord",
            "logo": {
              "@type": "ImageObject",
              "url": "https://equinord.dk/favicon.svg"
            }
          },
          "datePublished": post.date,
          "dateModified": post.date,
          "description": post.excerpt,
          "articleSection": post.category,
          "keywords": post.tags.join(', '),
          "wordCount": post.content.split(/\s+/).length
        }}
      />
      <Header />
      <main>
      
      {/* Breadcrumb */}
      <div className="bg-gradient-to-r from-gray-50 to-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <nav className="flex items-center space-x-3 text-sm">
            <Link to="/" className="text-emerald-600 hover:text-emerald-700 font-medium flex items-center space-x-1">
              <i className="ri-home-line"></i>
              <span>Hjem</span>
            </Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <Link to="/blog" className="text-emerald-600 hover:text-emerald-700 font-medium flex items-center space-x-1">
              <i className="ri-book-open-line"></i>
              <span>Blog</span>
            </Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-500 font-medium truncate">{post.title}</span>
          </nav>
        </div>
      </div>

      {/* Article */}
      <article className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <span className="px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full text-sm font-semibold">
                {post.category}
              </span>
              <div className="flex items-center text-gray-500 text-sm">
                <i className="ri-time-line mr-1"></i>
                <span>{post.readTime} læsning</span>
              </div>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-8 leading-tight">
              {post.title}
            </h1>
            
            <div className="flex items-center text-gray-600 mb-8">
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-emerald-600"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Af {post.author}</div>
                  <div className="text-sm text-gray-500">
                    {new Date(post.date).toLocaleDateString('da-DK', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Featured Image */}
          <div className="mb-12 aspect-[2/1]">
            <img
              src={post.image}
              alt={post.title}
              width="1200"
              height="600"
              className="w-full h-full object-cover rounded-2xl shadow-2xl"
              fetchPriority="high"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = `https://via.placeholder.com/1200x600/10b981/ffffff?text=${encodeURIComponent(post.title)}`;
              }}
            />
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
            <MarkdownRenderer content={post.content} />
          </div>

          {/* Tags */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags</h3>
            <div className="flex flex-wrap gap-3">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium hover:bg-emerald-100 transition-colors cursor-pointer"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {/* Share */}
          <div className="mt-12 bg-gradient-to-r from-emerald-50 to-gray-50 rounded-2xl p-8">
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Del dette indlæg</h3>
              <p className="text-gray-600 mb-6">Hjælp andre med at finde denne værdifulde information</p>
              <div className="flex items-center justify-center gap-4">
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Del på Facebook"
                  className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-all duration-200 hover:scale-110 shadow-lg"
                >
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Del på Twitter"
                  className="w-12 h-12 bg-sky-500 text-white rounded-full flex items-center justify-center hover:bg-sky-600 transition-all duration-200 hover:scale-110 shadow-lg"
                >
                  <i className="ri-twitter-line text-lg"></i>
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Del på LinkedIn"
                  className="w-12 h-12 bg-blue-700 text-white rounded-full flex items-center justify-center hover:bg-blue-800 transition-all duration-200 hover:scale-110 shadow-lg"
                >
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Flere Interessante Artikler
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Udforsk flere ekspertguides og tips om hesteudstyr og pleje
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {recentPosts
              .filter(p => p.id !== post.id)
              .slice(0, 3)
              .map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group border border-gray-100"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      width="400"
                      height="200"
                      loading="lazy"
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = `https://via.placeholder.com/800x400/10b981/ffffff?text=${encodeURIComponent(relatedPost.title)}`;
                      }}
                    />
                    <div className="absolute top-4 left-4 bg-emerald-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {relatedPost.category}
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <div className="flex items-center space-x-1">
                        <i className="ri-time-line"></i>
                        <span>{relatedPost.readTime}</span>
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-emerald-600 transition-colors leading-tight">
                      {relatedPost.title}
                    </h3>
                    <p className="text-gray-600 line-clamp-3 leading-relaxed">
                      {relatedPost.excerpt}
                    </p>
                    <div className="mt-4 flex items-center text-emerald-600 font-semibold group-hover:translate-x-1 transition-transform duration-200">
                      Læs mere
                      <i className="ri-arrow-right-line ml-2"></i>
                    </div>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      <ScrollToTop />
      </main>
      <Footer />
    </div>
  );
}

